import { Row, Table, Modal, Col } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import Button from "../../../../../components/functional_components/Button/PFButton";
import { useTranslation } from "react-i18next";
import Legend from "./Legend";
import TableLegend from "./TableLegend";
import { useDispatch, useSelector } from "react-redux";
import { setEditData } from "../../../../../redux/ducks/RiskAnalysis";
import { useParams } from "react-router-dom";
import { setLoading } from "../../../../../redux/ducks/Loading";
import { formatNumber, showError, showSuccess } from "../../../../../utils/utilities";
import RiskOpportunities from "../../../../../services/api/RisksOpportunities";
import { get, toLower } from "lodash";
import { dbDate, langMonth } from "../../../../../utils/properties";
import dayjs from "dayjs";
const { confirm } = Modal;

export default function Risks() {
  const dispatch = useDispatch();
  const params = useParams();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const [state, setState] = useState({
    legendTable: [],
    tableContent: [],
  });

  const columns = [
    {
      title: t("projectsManagement.riskAnalysis.table.month"),
      dataIndex: "month",
      key: "month",
      fixed: "left",
      width: 150,
      render: (date) => date !== null && dayjs(date.toString(), "YYYYMM").format(dateFormat),
    },
    {
      title: t("projectsManagement.riskAnalysis.table.type"),
      dataIndex: "type",
      key: "type",
      fixed: "left",
      width: 150,
      render: (amount) => {
        switch (amount) {
          case "COSTS":
            return t("projectsManagement.riskAnalysis.types.costs");
          case "CONTRACT_SOW":
            return t("projectsManagement.riskAnalysis.types.contractSow");
          case "CUSTOMER_RELATIONS":
            return t("projectsManagement.riskAnalysis.types.customerRelations");
          case "TEAM":
            return t("projectsManagement.riskAnalysis.types.team");
          case "OPERATIONAL_DELIVERY":
            return t("projectsManagement.riskAnalysis.types.operationalDelivery");
          case "OTHER":
            return t("projectsManagement.riskAnalysis.types.other");
          default:
            return amount;
        }
      },
    },
    {
      title: t("projectsManagement.riskAnalysis.table.riskDescription"),
      dataIndex: "description",
      key: "description",
      width: 150,
    },
    {
      title: t("projectsManagement.riskAnalysis.table.impactDescription"),
      dataIndex: "impactDescription",
      key: "impactDescription",
      width: 150,
    },
    {
      title: <div className="center">{t("projectsManagement.riskAnalysis.table.impactEvaluationInitial")}</div>,
      dataIndex: "impactEvaluationInitial",
      key: "impactEvaluationInitial",
      className: "table-value-right",
      width: 150,
      render: (amount) => formatNumber({ value: amount, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: <div className="center">{t("projectsManagement.riskAnalysis.table.impactEvaluationCurrent")}</div>,
      dataIndex: "impactEvaluationCurrent",
      key: "impactEvaluationCurrent",
      className: "table-value-right",
      width: 150,
      render: (amount) => formatNumber({ value: amount, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: <div className="center">{t("projectsManagement.riskAnalysis.table.impactLevel")}</div>,
      dataIndex: "impactLevel",
      key: "impactLevel",
      className: "table-value-center",
      width: 150,
      render: (record) => <div className="center">{record}</div>,
    },
    {
      title: <div className="center">{t("projectsManagement.riskAnalysis.table.probability")}</div>,
      dataIndex: "probably",
      key: "probably",
      className: "table-value-center",
      width: 150,
      render: (record) => <div className="center">{record}</div>,
    },
    {
      title: <div className="center">{t("projectsManagement.riskAnalysis.table.score")}</div>,
      dataIndex: "score",
      key: "score",
      className: "table-value-center",
      width: 150,
      render: (amount) => {
        if (amount <= 1) {
          return <b className="lowest space-around p-10 center">{amount}</b>;
        }
        if (amount <= 2) {
          return <b className="veryLow space-around p-10 center">{amount}</b>;
        }
        if (amount <= 3) {
          return <b className="low space-around p-10 center">{amount}</b>;
        }
        if (amount <= 4) {
          return <b className="mediumLow space-around p-10 center">{amount}</b>;
        }
        if (amount <= 6) {
          return <b className="mediumHigh space-around p-10 center">{amount}</b>;
        }
        if (amount <= 9) {
          return <b className="highest space-around p-10 center">{amount}</b>;
        }
      },
    },
    {
      title: <div className="center">{t("projectsManagement.riskAnalysis.table.riskLevel")}</div>,
      dataIndex: "level",
      key: "level",
      className: "table-value-center",
      width: 200,
      render: (amount) => {
        switch (amount) {
          case "LOWEST":
            return (
              <div className="lowest space-around p-10 center">
                {t("projectsManagement.riskAnalysis.legend.lowest")}
              </div>
            );
          case "VERY_LOW":
            return (
              <div className="veryLow space-around p-10 center">
                {t("projectsManagement.riskAnalysis.legend.veryLow")}
              </div>
            );
          case "LOW":
            return (
              <div className="low space-around p-10 center">{t("projectsManagement.riskAnalysis.legend.low")}</div>
            );
          case "MEDIUM_LOW":
            return (
              <div className="mediumLow space-around p-10 center">
                {t("projectsManagement.riskAnalysis.legend.mediumLow")}
              </div>
            );
          case "MEDIUM_HIGH":
            return (
              <div className="mediumHigh space-around p-10 center">
                {t("projectsManagement.riskAnalysis.legend.mediumHigh")}
              </div>
            );
          case "HIGHEST":
            return (
              <div className="highest space-around p-10 center">
                {t("projectsManagement.riskAnalysis.legend.highest")}
              </div>
            );
          default:
            return <div className="space-around">{amount}</div>;
        }
      },
    },
    {
      title: t("projectsManagement.riskAnalysis.table.actionPlan"),
      dataIndex: "actionPlan",
      key: "actionPlan",
      width: 150,
    },
    {
      title: t("projectsManagement.riskAnalysis.table.actionOwner"),
      dataIndex: "actionOwner",
      key: "actionOwner",
      width: 150,
    },
    {
      title: t("projectsManagement.riskAnalysis.table.dueDate"),
      dataIndex: "dueDate",
      key: "dueDate",
      width: 150,
      render: (date) => date !== null && dayjs(date, dbDate).format(dateFormat),
    },
    {
      title: t("projectsManagement.riskAnalysis.table.status"),
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (amount) => {
        switch (amount) {
          case "TO_BE_EVALUATED":
            return t("projectsManagement.riskAnalysis.status.toBeEvaluated");
          case "EVALUATING":
            return t("projectsManagement.riskAnalysis.status.evaluating");
          case "ADDRESSED":
            return t("projectsManagement.riskAnalysis.status.addressed");
          case "ABSORBER":
            return t("projectsManagement.riskAnalysis.status.absorber");
          default:
            return amount;
        }
      },
    },
    {
      dataIndex: "",
      key: "x",
      width: 80,
      fixed: "right",
      render: (record) => {
        return (
          <div className="space-around">
            <div onClick={() => userInfo.canEditRiskAnalysis && goToRiskAnalysisDetail(record, false)}>
              <EditOutlined
                className={userInfo.canEditRiskAnalysis ? "icon-small" : "icon-small noCursorPointer disabled"}
              />
            </div>
            <div
              onClick={() =>
                userInfo.canEditRiskAnalysis && projectManagement.approved && record.editable && handleDelete(record)
              }
            >
              <DeleteOutlined
                className={
                  userInfo.canEditRiskAnalysis && projectManagement.approved && record.editable
                    ? "icon-small"
                    : "icon-small noCursorPointer disabled"
                }
              />
            </div>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    dataList();
  }, []);

  const goToRiskAnalysisDetail = (project, isNew = true) => {
    dispatch(setEditData("risk", params.id, project.id, isNew));
  };

  const handleDelete = async (record) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const riskDelete = await RiskOpportunities.delete(record.id);
        if (riskDelete) {
          if (!riskDelete.error) {
            showSuccess();
            dataList();
          } else showError(get(riskDelete, "error.messageCode", "ER000"));
        } else showError(t("projectsManagement.riskAnalysis.cantDelete"));
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const dataList = async () => {
    dispatch(setLoading(true));
    const services = await RiskOpportunities.riskList(params.id);
    if (!services.error) {
      let legendTableContent = [];
      if (services.data[0]) {
        services.data[0].risks.forEach((client, i) => {
          client.key = i;
        });
        legendTableContent = [
          {
            title: t("projectsManagement.riskAnalysis.legend.overllImpact"),
            initial: services.data[0].sumOverallImpactInitial,
            current: services.data[0].sumOverallImpactCurrent,
            key: 0,
          },
          {
            title: t("projectsManagement.riskAnalysis.legend.higherRating"),
            initial: services.data[0].sumHigherRatingInitial,
            current: services.data[0].sumHigherRatingCurrent,
            key: 1,
          },
        ];
      }
      setState((prevState) => ({
        ...prevState,
        legendTable: legendTableContent,
        tableContent: get(services, "data[0].risks", []),
      }));
    } else showError(get(services, "error.messageCode", "ER000"));
    dispatch(setLoading(false));
  };

  return (
    <>
      <Row type="flex" align="middle" justify="space-between" className="mb-10">
        <Col sm={12} md={24} lg={12}>
          <Legend />
        </Col>
        <Col sm={12} md={24} lg={8}>
          <TableLegend data={state.legendTable} />
        </Col>
      </Row>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          isDisabled={!userInfo.canEditRiskAnalysis}
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={goToRiskAnalysisDetail}
        />
      </Row>
      <Table
        bordered
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={false}
        scroll={{ x: 1250 }}
      />
    </>
  );
}
